
import React, { useEffect, useState } from "react";
import apiRequest from "../../../helpers/apiRequest";
import { Urls } from "../../../utils/urls";
import Loading from "../../../components/loading/Loading";
import TextInputComp from "../../../components/inputs/TextInputComp";
import ModalBase from "../../../components/modals/ModalBase";
import { TrashIcon, PlusIcon, PencilIcon } from "@heroicons/react/24/outline";

export type EDLevel = {
    _id: string;
    name: string;
};

const EducationLevels = () => {
    const [edLevels, setEdLevels] = useState<EDLevel[]>([]);
    const [loading, setLoading] = useState(true);
    const [elName, setElName] = useState("");
    const [editingLevel, setEditingLevel] = useState<EDLevel | null>(null);
    const [editName, setEditName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getAllEls = async () => {
        setLoading(true);
        const { data, error } = await apiRequest<{ educational_levels: EDLevel[] }>(
            {
                method: "GET",
                url: Urls.all_ed_levels
            }
        );
        if (error) {
            alert(error);
        } else if (data) {
            setEdLevels(data?.educational_levels);
        }
        setLoading(false);
    };

    const onDeletePress = async (id: string) => {
        const confirm = window.confirm("Are you sure you want to delete this education level?");
        if (!confirm) return;
        const { data, error } = await apiRequest<any>({
            method: "DELETE",
            url: `${Urls.all_ed_levels}/${id}`
        });
        if (error) {
            alert(error);
        } else if (data) {
            getAllEls();
        }
    };

    const onAddEducationalLevel = async () => {
        if (!elName) return alert("Please enter a name");
        setIsSubmitting(true);
        const { data, error } = await apiRequest<any>({
            method: "POST",
            url: Urls.all_ed_levels,
            data: { name: elName }
        });
        if (error) {
            alert(error);
        } else if (data) {
            setElName("");
            getAllEls();
        }
        setIsSubmitting(false);
    };

    const onUpdateLevel = async () => {
        if (!editingLevel || !editName) return;
        setIsSubmitting(true);
        const { data, error } = await apiRequest<any>({
            method: "PATCH",
            url: `${Urls.all_ed_levels}/${editingLevel._id}`,
            data: { name: editName }
        });
        if (error) {
            alert(error);
        } else if (data) {
            setEditingLevel(null);
            getAllEls();
        }
        setIsSubmitting(false);
    };

    useEffect(() => {
        getAllEls();
    }, []);

    if (loading && edLevels.length === 0) return <Loading />;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Add Section */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Level</h3>
                <div className="flex items-end gap-3">
                    <div className="flex-1">
                        <TextInputComp
                            placeholder="e.g. Primary School, High School"
                            onChange={(e) => setElName(e.target.value)}
                            value={elName}
                        />
                    </div>
                    <button 
                        onClick={onAddEducationalLevel}
                        disabled={isSubmitting || !elName}
                        className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PlusIcon className="w-5 h-5 text-white" />
                        Add Level
                    </button>
                </div>
            </div>

            {/* List Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Existing Levels</h3>
                {edLevels.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200">
                        <p className="text-gray-400">No education levels added yet.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {edLevels.map((el) => (
                            <div 
                                key={el._id}
                                className="group p-4 flex items-center justify-between bg-white border border-gray-100 rounded-2xl shadow-sm hover:border-primary/30 hover:shadow-md transition-all duration-300"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-xl text-primary font-bold">
                                        {el.name.charAt(0).toUpperCase()}
                                    </div>
                                    <p className="font-semibold text-gray-700">{el.name}</p>
                                </div>

                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => {
                                            setEditingLevel(el);
                                            setEditName(el.name);
                                        }}
                                        className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                                        title="Edit"
                                    >
                                        <PencilIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDeletePress(el._id)}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                        title="Delete"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            <ModalBase
                isOpen={!!editingLevel}
                onClose={() => setEditingLevel(null)}
                title="Edit Education Level"
                primaryBtn={{
                    title: isSubmitting ? "Saving..." : "Update Level",
                    onClick: onUpdateLevel,
                    disabled: !editName || isSubmitting
                }}
                secondaryBtn={{
                    title: "Cancel",
                    onClick: () => setEditingLevel(null)
                }}
            >
                <div className="py-2">
                    <TextInputComp
                        label="Level Name"
                        placeholder="e.g. Higher Secondary"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                    />
                </div>
            </ModalBase>
        </div>
    );
};

export default EducationLevels;
